/**
 * Age-range filter model, shared by the People filter, the Group filter and
 * the Create-group form — moved out of `home.tsx` so the slider component and
 * the root tests can reach it without importing the screen.
 *
 * The stored value is a label string, not a pair of numbers, because that is
 * what already lives in filter state and on `group.ageRange` server-side:
 * "Any age" (the unfiltered sentinel), "26-40", or "50+" (open-ended above
 * the slider ceiling). `formatAgeRange` and `parseAgeRangeForSlider` are
 * exact inverses over every value the slider can produce, so re-opening a
 * filter never moves a saved range.
 *
 * Zero imports on purpose: the root suite runs this file under `node --test`,
 * where `react-native` and `@/` aliases do not resolve.
 */

export const AGE_RANGE_MIN = 18;
export const AGE_RANGE_SLIDER_MAX = 75;
// What "75+" means when matching people: open-ended, not capped at the
// slider's own ceiling.
export const AGE_RANGE_OPEN_MAX = 120;
export const ANY_AGE_RANGE_LABEL = "Any age";

export type NumericAgeRange = {
  min: number;
  max: number;
};

export function clampAge(value: number): number {
  return Math.min(
    AGE_RANGE_SLIDER_MAX,
    Math.max(AGE_RANGE_MIN, Math.round(value)),
  );
}

export function normalizeAgeRange(range: NumericAgeRange): NumericAgeRange {
  const min = clampAge(range.min);
  const max = clampAge(range.max);

  return min <= max ? { min, max } : { min: max, max: min };
}

export function formatAgeRange(range: NumericAgeRange): string {
  const normalized = normalizeAgeRange(range);
  if (
    normalized.min <= AGE_RANGE_MIN &&
    normalized.max >= AGE_RANGE_SLIDER_MAX
  ) {
    return ANY_AGE_RANGE_LABEL;
  }
  if (normalized.max >= AGE_RANGE_SLIDER_MAX) {
    return `${normalized.min}+`;
  }
  return `${normalized.min}-${normalized.max}`;
}

export function parseAgeRangeForSlider(
  value: string | null | undefined,
): NumericAgeRange {
  if (!value || value.toLowerCase() === ANY_AGE_RANGE_LABEL.toLowerCase()) {
    return { min: AGE_RANGE_MIN, max: AGE_RANGE_SLIDER_MAX };
  }

  const trimmed = value.trim();
  if (trimmed.endsWith("+")) {
    const min = Number.parseInt(trimmed, 10);
    return normalizeAgeRange({
      min: Number.isFinite(min) ? min : AGE_RANGE_MIN,
      max: AGE_RANGE_SLIDER_MAX,
    });
  }

  const [minValue, maxValue] = trimmed.split("-");
  const min = Number.parseInt(minValue ?? "", 10);
  const max = Number.parseInt(maxValue ?? "", 10);

  return normalizeAgeRange({
    min: Number.isFinite(min) ? min : AGE_RANGE_MIN,
    max: Number.isFinite(max) ? max : AGE_RANGE_SLIDER_MAX,
  });
}

export function parseAgeRangeForMatch(
  value: string | null | undefined,
): NumericAgeRange | null {
  if (!value || value.toLowerCase() === ANY_AGE_RANGE_LABEL.toLowerCase()) {
    return null;
  }

  const sliderRange = parseAgeRangeForSlider(value);
  return {
    min: sliderRange.min,
    max:
      value.trim().endsWith("+") || sliderRange.max >= AGE_RANGE_SLIDER_MAX
        ? AGE_RANGE_OPEN_MAX
        : sliderRange.max,
  };
}

export function ageRangesOverlap(
  first: NumericAgeRange | null,
  second: NumericAgeRange | null,
): boolean {
  if (!first || !second) return true;
  return first.min <= second.max && second.min <= first.max;
}
